import { Component, OnInit, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DatePickerModule } from 'primeng/datepicker';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ChartModule } from 'primeng/chart';
import { MultiSelect } from 'primeng/multiselect';
import { Store } from '@ngrx/store';
import { filter, take } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';

import { AppButton } from '../../../../shared/components/app-button/app-button';
import { RoomsService } from '../../../../core/services/rooms.service';
import { selectUserInfo } from '../../../../core/store/auth/auth.selectors';
import {
    AvailableDoctor,
    DoctorWorkload,
    RoomCapacityPressure,
    WorkloadReportData,
    WorkloadSource
} from '../../../../core/models/workload.model';

/** A doctor's row in the heatmap: the professional plus one cell per day in range. */
interface HeatRow {
    doctor_id: number;
    doctor_name: string;
    cells: HeatCell[];
}

interface HeatCell {
    day: string;
    /** Null means the professional had no appointments that day — not zero workload. */
    percentage: number | null;
    bookedHours: number;
    overlapHours: number;
    apptCount: number;
    source: WorkloadSource | null;
    isWeekend: boolean;
}

@Component({
    selector: 'app-workload-report',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        DatePickerModule,
        ButtonModule,
        TooltipModule,
        ChartModule,
        MultiSelect,
        AppButton
    ],
    templateUrl: './workload-report.html'
})
export class WorkloadReport implements OnInit {
    report: WorkloadReportData | null = null;
    loading = false;
    isFetching = false;

    /** Monotonic id; only the newest in-flight request may write to the view. */
    private requestSeq = 0;

    heatRows: HeatRow[] = [];
    dayAxis: { day: string; label: string; dow: string; isWeekend: boolean }[] = [];

    chartData: any = null;
    chartOptions: any = null;

    trendData: any = null;
    trendOptions: any = null;

    /**
     * Every professional with work in the range. Held separately from
     * report.doctors, which is the filtered set — reading the dropdown off the
     * filtered set would leave no way to widen a selection again.
     */
    availableDoctors: AvailableDoctor[] = [];
    selectedDoctorIds: number[] = [];

    startDate: Date | null = null;
    endDate: Date | null = null;

    /**
     * Latest selectable day, pinned to the END of today.
     *
     * Two reasons it is not a bare `new Date()`. Capping at "now" would make any
     * value set a moment later — filterToday() calling new Date() again — strictly
     * greater than the max, and the picker then refuses to render it, which reads
     * to the user as "Today did nothing". And a future day contributes capacity
     * hours that cannot possibly have bookings yet, dragging every percentage
     * down as a pure artefact of the filter.
     */
    readonly maxDate = WorkloadReport.endOfDay(new Date());

    /** Local midnight. Every date leaving this component is normalised to it. */
    private static startOfDay(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    private static endOfDay(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
    }

    /**
     * Workload bands. Note these invert the occupancy report's meaning: there,
     * a high percentage is a room earning its keep. Here it is a person with no
     * slack, so the scale runs good-in-the-middle.
     */
    readonly bands = [
        { key: 'WORKLOAD_BAND_LIGHT', min: 0, max: 40, hex: '#64748b' },
        { key: 'WORKLOAD_BAND_HEALTHY', min: 40, max: 85, hex: '#10b981' },
        { key: 'WORKLOAD_BAND_HEAVY', min: 85, max: 100, hex: '#f59e0b' },
        { key: 'WORKLOAD_BAND_OVER', min: 100, max: Infinity, hex: '#f43f5e' }
    ];

    private store = inject(Store);
    private userInfo$ = this.store.select(selectUserInfo);
    private translate = inject(TranslateService);

    constructor(
        private roomsService: RoomsService,
        private cdr: ChangeDetectorRef
    ) { }

    async ngOnInit() {
        const { start, end } = this.getDefaultRange();
        this.startDate = start;
        this.endDate = end;
        await this.load();
    }

    /** Month-to-date, never running past today. */
    private getDefaultRange(): { start: Date; end: Date } {
        const now = new Date();
        return {
            start: new Date(now.getFullYear(), now.getMonth(), 1),
            end: WorkloadReport.startOfDay(now)
        };
    }

    private isSameDay(a: Date | null, b: Date): boolean {
        return !!a && a.getFullYear() === b.getFullYear()
            && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    }

    get hasCustomFilters(): boolean {
        const { start, end } = this.getDefaultRange();
        return this.selectedDoctorIds.length > 0
            || !this.isSameDay(this.startDate, start)
            || !this.isSameDay(this.endDate, end);
    }

    get doctors(): DoctorWorkload[] {
        return this.report?.doctors ?? [];
    }

    get rooms(): RoomCapacityPressure[] {
        return this.report?.rooms ?? [];
    }

    /**
     * True when any doctor-day fell back off a real roster. Drives the banner
     * that tells the reader these are estimates — production currently has
     * almost no schedule_availability_tb rows, so this is the normal case, and
     * silently presenting an estimate as a measurement would be worse than
     * showing nothing.
     */
    get usesEstimatedHours(): boolean {
        const s = this.report?.sources;
        if (!s) return false;
        return Object.keys(s).some(k => k !== 'shifts');
    }

    /** Doctor-days resting on the observed-span or clinic-hours rungs. */
    get estimatedDayCount(): number {
        const s = this.report?.sources;
        if (!s) return 0;
        return Object.entries(s)
            .filter(([key]) => key !== 'shifts')
            .reduce((acc, [, count]) => acc + (Number(count) || 0), 0);
    }

    /** Professionals booked into two places at once at some point in the range. */
    get doubleBookedDoctors(): DoctorWorkload[] {
        return this.doctors.filter(d => d.overlap_hours > 0);
    }

    /** Grows with the roster so bars stay a readable thickness rather than squashing. */
    get chartHeight(): number {
        return Math.max(160, this.doctors.length * 38 + 60);
    }

    /**
     * Column track for the heatmap grid.
     *
     * The name column sizes to its content between a floor and a ceiling, so
     * short rosters don't waste a fixed 9rem and long names don't eat the grid.
     * Day columns are minmax(22px, 1fr): they stretch to fill the card when the
     * range is short, and once the range is long enough that 22px each exceeds
     * the container, the grid overflows and its wrapper scrolls rather than
     * squeezing cells below a clickable size.
     */
    get heatGridColumns(): string {
        const days = this.dayAxis.length || 1;
        return `minmax(7rem, clamp(7rem, 16%, 14rem)) repeat(${days}, minmax(22px, 1fr))`;
    }

    /**
     * True while the professional multi-select is narrowing the page.
     *
     * Room capacity pressure deliberately ignores that filter: capacity counts
     * every patient in the room, so scoping it to one professional would
     * produce a number that means nothing. But then the room figures stay
     * clinic-wide while everything around them is filtered, which is invisible
     * unless the UI says so — hence the marker this drives.
     */
    get isProfessionalFiltered(): boolean {
        return this.selectedDoctorIds.length > 0;
    }

    /**
     * Categorical slots for the trend lines, in fixed order. Validated for
     * colourblind separation on a light surface; three slots sit under 3:1
     * contrast against white, which the detail table relieves by repeating
     * every value as text.
     */
    private readonly seriesPalette = [
        '#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300'
    ];

    /**
     * doctor_id -> slot. Assigned over the displayed professionals sorted by
     * NAME, never by workload: ordering by the measured value would make colour
     * a second, redundant encoding of magnitude, and every filter change would
     * repaint the survivors as the ranking shifted.
     *
     * With a roster larger than the palette, perfect stability across arbitrary
     * selections isn't achievable — but the trend only draws at or under the
     * cap, and name order at least keeps assignment deterministic.
     */
    private seriesColours = new Map<number, string>();

    private buildSeriesColours() {
        this.seriesColours.clear();
        [...this.heatRows]
            .sort((a, b) => a.doctor_name.localeCompare(b.doctor_name))
            .forEach((row, i) => {
                this.seriesColours.set(row.doctor_id, this.seriesPalette[i % this.seriesPalette.length]);
            });
    }

    /** Past this many lines the chart stops being readable and folds away. */
    readonly trendSeriesCap = 6;

    /** True when there are too many professionals on screen to draw one line each. */
    get trendTooManySeries(): boolean {
        return this.doctors.length > this.trendSeriesCap;
    }

    async applyFilters() {
        await this.load();
    }

    async clearFilters() {
        this.selectedDoctorIds = [];
        const { start, end } = this.getDefaultRange();
        this.startDate = start;
        this.endDate = end;
        await this.load();
    }

    async filterToday() {
        // Separate instances at local midnight: a shared object would have both
        // pickers mutating one value, and a time component would push the model
        // past maxDate.
        const now = new Date();
        this.startDate = WorkloadReport.startOfDay(now);
        this.endDate = WorkloadReport.startOfDay(now);
        await this.load();
    }

    async load() {
        // Deliberately NOT skipped while another request is in flight. The old
        // early-return dropped any filter click made during a load, leaving the
        // pickers showing one range and the figures belonging to another. A
        // sequence token keeps the newest request authoritative instead.
        const token = ++this.requestSeq;

        this.isFetching = true;
        this.loading = true;
        this.cdr.detectChanges();

        try {
            const userInfo = await firstValueFrom(
                this.userInfo$.pipe(filter(u => !!u), take(1))
            ) as any;

            const branchId = userInfo?.branches_id || userInfo?.branch_id;
            if (!branchId) {
                console.error('No branch ID found for user');
                return;
            }

            const { data, error } = await this.roomsService.getWorkloadReport(
                branchId,
                this.startDate ? this.toDateOnlyString(this.startDate) : '',
                this.endDate ? this.toDateOnlyString(this.endDate) : '',
                this.selectedDoctorIds
            );

            // A late reply from a superseded request must not overwrite newer data.
            if (token !== this.requestSeq) return;

            if (error || !data) {
                console.error('Error fetching workload report', error);
                this.report = null;
                this.heatRows = [];
                this.dayAxis = [];
                this.chartData = null;
                this.trendData = null;
                return;
            }

            this.report = data;

            // Echo the range the server actually used back into the pickers.
            // get_workload_report_v1 resolves blanks and caps the end at today,
            // so without this the controls can claim one range while the figures
            // below them describe another — and any disagreement between what
            // was asked and what was answered becomes invisible.
            this.syncPickersToRange(data);

            // Always the unfiltered roster, so narrowing the selection never
            // removes the options needed to widen it again.
            this.availableDoctors = data.available_doctors || [];
            this.buildDayAxis(data);
            this.buildHeatRows(data);
            this.buildChart(data);
            this.buildTrend();
        } catch (e) {
            console.error('Exception fetching workload report', e);
        } finally {
            this.loading = false;
            this.isFetching = false;
            this.cdr.detectChanges();
        }
    }

    /**
     * Point the pickers at the range the report actually covers.
     *
     * Only writes when the day differs, so it never replaces a Date instance the
     * picker is already happy with — a new object on every load would make the
     * calendar overlay flicker and re-render for no reason.
     */
    private syncPickersToRange(report: WorkloadReportData) {
        const start = this.parseDateOnly(report.range?.start);
        const end = this.parseDateOnly(report.range?.end);
        if (start && !this.isSameDay(this.startDate, start)) this.startDate = start;
        if (end && !this.isSameDay(this.endDate, end)) this.endDate = end;
    }

    /** Human-readable range the figures actually describe, for the header. */
    get rangeLabel(): string {
        const start = this.parseDateOnly(this.report?.range?.start);
        const end = this.parseDateOnly(this.report?.range?.end);
        if (!start || !end) return '';
        const fmt = (d: Date) => d.toLocaleDateString(this.translate.currentLang || undefined,
            { day: 'numeric', month: 'short', year: 'numeric' });
        return this.isSameDay(start, end) ? fmt(start) : `${fmt(start)} — ${fmt(end)}`;
    }

    /**
     * The heatmap axis is every calendar day in range, not just days that have
     * data. Gaps are the point: a professional's empty Tuesdays should read as
     * empty, not be closed up into a continuous block of work.
     */
    private buildDayAxis(report: WorkloadReportData) {
        const axis: typeof this.dayAxis = [];
        const start = this.parseDateOnly(report.range.start);
        const end = this.parseDateOnly(report.range.end);
        if (!start || !end) { this.dayAxis = []; return; }

        const cursor = new Date(start);
        // Hard stop: a year of columns is unreadable and would hang the render.
        let guard = 0;
        while (cursor <= end && guard++ < 400) {
            const dow = cursor.getDay();
            axis.push({
                day: this.toDateOnlyString(cursor),
                label: String(cursor.getDate()),
                dow: this.translate.instant('WEEKDAY_SHORT_' + dow),
                isWeekend: dow === 0 || dow === 6
            });
            cursor.setDate(cursor.getDate() + 1);
        }
        this.dayAxis = axis;
    }

    private buildHeatRows(report: WorkloadReportData) {
        const byDoctor = new Map<number, Map<string, typeof report.heatmap[number]>>();
        for (const cell of report.heatmap || []) {
            if (!byDoctor.has(cell.doctor_id)) byDoctor.set(cell.doctor_id, new Map());
            byDoctor.get(cell.doctor_id)!.set(this.normaliseDay(cell.day), cell);
        }

        this.heatRows = (report.doctors || []).map(doc => ({
            doctor_id: doc.doctor_id,
            doctor_name: doc.doctor_name,
            cells: this.dayAxis.map(d => {
                const hit = byDoctor.get(doc.doctor_id)?.get(d.day);
                return {
                    day: d.day,
                    percentage: hit ? hit.workload_percentage : null,
                    bookedHours: hit ? hit.booked_hours : 0,
                    overlapHours: hit ? hit.overlap_hours : 0,
                    apptCount: hit ? hit.appt_count : 0,
                    source: hit ? hit.workload_source : null,
                    isWeekend: d.isWeekend
                };
            })
        }));
    }

    /**
     * Horizontal bars: the labels are professionals' names, which need room to
     * read, and the comparison is magnitude across identities.
     */
    private buildChart(report: WorkloadReportData) {
        const docs = (report.doctors || []).filter(d => d.workload_percentage != null);
        if (!docs.length) { this.chartData = null; return; }

        const peak = Math.max(100, ...docs.map(d => d.workload_percentage || 0));

        this.chartData = {
            labels: docs.map(d => d.doctor_name),
            datasets: [{
                label: this.translate.instant('WORKLOAD_PERCENT'),
                data: docs.map(d => d.workload_percentage),
                backgroundColor: docs.map(d => this.bandHex(d.workload_percentage)),
                borderRadius: 4,
                borderSkipped: false,
                barThickness: 16,
                // Carried through so the tooltip can show hours next to the
                // percentage — 100% of a two-hour day is not an overload.
                bookedHours: docs.map(d => d.booked_hours)
            }]
        };

        this.chartOptions = {
            indexAxis: 'y',
            maintainAspectRatio: false,
            responsive: true,
            layout: { padding: { right: 16 } },
            plugins: {
                legend: { display: false },
                datalabels: { display: false },
                tooltip: {
                    callbacks: {
                        label: (ctx: any) => {
                            const hours = ctx.dataset.bookedHours?.[ctx.dataIndex];
                            const pct = `${ctx.parsed.x}%`;
                            return hours != null ? `${pct} · ${hours}h` : pct;
                        }
                    }
                }
            },
            scales: {
                x: {
                    beginAtZero: true,
                    suggestedMax: Math.ceil(peak / 10) * 10,
                    ticks: { callback: (v: any) => `${v}%`, color: '#94a3b8', font: { size: 11 } },
                    grid: { color: '#f1f5f9', drawTicks: false },
                    border: { display: false }
                },
                y: {
                    ticks: { color: '#475569', font: { size: 12 } },
                    grid: { display: false },
                    border: { display: false }
                }
            }
        };
    }

    /**
     * Daily workload per professional, one line each.
     *
     * The heatmap already carries this data, but colour bands can only say
     * "heavy" or "light" — once you have picked two or three people to compare,
     * the question becomes whether one is climbing while another is flat, and
     * only a shared y-axis answers that.
     *
     * Days with no appointments stay null rather than becoming zero: a blank
     * Tuesday is a day off, not a day of no work, and spanGaps is off so the
     * line breaks instead of drawing straight through it.
     */
    private buildTrend() {
        const rows = this.heatRows;

        if (!rows.length || this.dayAxis.length < 2 || this.trendTooManySeries) {
            this.trendData = null;
            this.seriesColours.clear();
            return;
        }

        this.buildSeriesColours();

        this.trendData = {
            labels: this.dayAxis.map(d => d.label),
            datasets: rows.map(row => {
                const hex = this.seriesColours.get(row.doctor_id) ?? this.seriesPalette[0];
                return {
                    label: row.doctor_name,
                    data: row.cells.map(c => c.percentage),
                    borderColor: hex,
                    backgroundColor: hex,
                    borderWidth: 2,
                    pointRadius: this.dayAxis.length <= 45 ? 2.5 : 0,
                    pointHoverRadius: 5,
                    pointBackgroundColor: hex,
                    tension: 0.25,
                    spanGaps: false
                };
            })
        };

        this.trendOptions = {
            maintainAspectRatio: false,
            responsive: true,
            interaction: { mode: 'index', intersect: false },
            plugins: {
                // Always present: identity must never rest on colour alone.
                legend: {
                    position: 'bottom',
                    labels: {
                        boxWidth: 10, boxHeight: 10, usePointStyle: true,
                        pointStyle: 'circle', color: '#475569',
                        font: { size: 11 }, padding: 16
                    }
                },
                datalabels: { display: false },
                tooltip: {
                    callbacks: {
                        title: (items: any[]) => {
                            const idx = items?.[0]?.dataIndex ?? 0;
                            return this.dayAxis[idx]?.day ?? '';
                        },
                        label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y}%`
                    }
                }
            },
            scales: {
                x: {
                    ticks: { color: '#94a3b8', font: { size: 10 }, maxRotation: 0, autoSkipPadding: 12 },
                    grid: { display: false },
                    border: { display: false }
                },
                y: {
                    beginAtZero: true,
                    ticks: { callback: (v: any) => `${v}%`, color: '#94a3b8', font: { size: 11 } },
                    grid: { color: '#f1f5f9', drawTicks: false },
                    border: { display: false }
                }
            }
        };
    }

    /** Re-query on every selection change — the filter is applied server-side. */
    async onDoctorSelectionChange() {
        await this.load();
    }

    /** Colour for a professional's trend line, mirrored onto the detail table. */
    seriesColour(doctorId: number): string | null {
        return this.seriesColours.get(doctorId) ?? null;
    }

    // ---- formatting helpers -------------------------------------------------

    formatPercentage(value: number | null): string {
        return value == null || isNaN(value) ? '—' : `${Number(value).toFixed(1)}%`;
    }

    formatHours(value: number | null): string {
        return value == null || isNaN(value) ? '—' : `${Number(value).toFixed(1)}h`;
    }

    private bandHex(value: number | null): string {
        if (value == null || isNaN(value)) return '#e2e8f0';
        return (this.bands.find(b => value >= b.min && value < b.max) ?? this.bands[3]).hex;
    }

    /** Tailwind classes for a workload pill. High is a warning here, not a win. */
    workloadClass(value: number | null): string {
        if (value == null || isNaN(value)) return 'bg-slate-50 text-slate-400';
        if (value > 100) return 'bg-rose-50 text-rose-600';
        if (value >= 85) return 'bg-amber-50 text-amber-600';
        if (value >= 40) return 'bg-emerald-50 text-emerald-600';
        return 'bg-slate-100 text-slate-500';
    }

    /** Background for a heatmap cell. Null (no appointments) is not zero. */
    cellStyle(cell: HeatCell): { [k: string]: string } {
        if (cell.percentage == null) {
            return { 'background-color': cell.isWeekend ? '#f8fafc' : '#f1f5f9' };
        }
        return { 'background-color': this.bandHex(cell.percentage) };
    }

    /**
     * Everything the cell colour cannot say: the hours behind the percentage,
     * how many patients, and whether the denominator was measured or estimated.
     */
    cellTooltip(row: HeatRow, cell: HeatCell): string {
        if (cell.percentage == null) {
            return `${row.doctor_name} · ${cell.day} · ${this.translate.instant('WORKLOAD_NO_APPOINTMENTS')}`;
        }
        // Single line with middots: PrimeNG escapes tooltip content by default
        // (correctly — these strings carry user-entered names), so a newline
        // would render as a space rather than a break.
        const parts = [
            `${row.doctor_name} · ${cell.day}`,
            `${this.formatPercentage(cell.percentage)} (${this.formatHours(cell.bookedHours)})`,
            `${cell.apptCount} ${this.translate.instant('WORKLOAD_APPOINTMENTS')}`
        ];
        if (cell.overlapHours > 0) {
            parts.push(`${this.formatHours(cell.overlapHours)} ${this.translate.instant('WORKLOAD_DOUBLE_BOOKED')}`);
        }
        if (cell.source) {
            parts.push(this.translate.instant('WORKLOAD_SOURCE_' + cell.source.toUpperCase()));
        }
        return parts.join(' · ');
    }

    sourceLabel(source: WorkloadSource): string {
        return this.translate.instant('WORKLOAD_SOURCE_' + source.toUpperCase());
    }

    /** A roster is a measurement; everything below it is an estimate. */
    sourceClass(source: WorkloadSource): string {
        return source === 'shifts'
            ? 'bg-emerald-50 text-emerald-600'
            : 'bg-slate-100 text-slate-500';
    }

    /** How full a room got at its busiest, as a share of its chairs. */
    capacityRatio(room: RoomCapacityPressure): number {
        if (!room.capacity) return 0;
        return Math.min(100, (room.peak_concurrency / room.capacity) * 100);
    }

    capacityBarClass(room: RoomCapacityPressure): string {
        if (room.days_over_capacity > 0) return 'bg-rose-500';
        if (room.capacity && room.peak_concurrency >= room.capacity) return 'bg-amber-500';
        return 'bg-emerald-500';
    }

    trackByDoctor = (_: number, row: HeatRow) => row.doctor_id;
    trackByCell = (_: number, cell: HeatCell) => cell.day;
    trackByRoom = (_: number, room: RoomCapacityPressure) => room.office_id;

    // ---- date helpers -------------------------------------------------------

    /**
     * toISOString() converts to UTC first, which shifts the calendar day for any
     * timezone ahead of UTC. Use the date's own local parts so what the user
     * picked is what gets sent — and so heatmap keys line up with the axis.
     */
    private toDateOnlyString(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    /** Postgres dates arrive as 'YYYY-MM-DD'; trim any time part defensively. */
    private normaliseDay(value: string | null | undefined): string {
        return (value || '').slice(0, 10);
    }

    /** Parse 'YYYY-MM-DD' as a LOCAL date — new Date(str) would treat it as UTC. */
    private parseDateOnly(value: string | null | undefined): Date | null {
        if (!value) return null;
        const parts = this.normaliseDay(value).split('-').map(Number);
        if (parts.length !== 3 || parts.some(isNaN)) return null;
        return new Date(parts[0], parts[1] - 1, parts[2]);
    }
}
